import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { taskApi, userApi, departmentApi, settingsApiFull } from '../api';
import { useAuth } from '../context/AuthContext';
import { Btn, Alert, Input, Spinner } from '../components/ui';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

function assignableEmployees(employees, user) {
  return employees.filter(e => {
    if (!e.active) return false;
    if (user?.role === 'SUPER_ADMIN' && e.id === user.id) return false;
    return true;
  });
}

export default function EditTask() {
  const { id }     = useParams();
  const { user }   = useAuth();
  const navigate   = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [depts,     setDepts]     = useState([]);
  const [tags,      setTags]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [saving,    setSaving]    = useState(false);

  const [form, setForm] = useState({
    title: '', description: '', assignedToId: '',
    departmentId: '', priority: 'MEDIUM', deadline: '', tags: [],
  });

  useEffect(() => {
    if (user?.role === 'EMPLOYEE') {
      navigate(`/tasks/${id}`, { replace: true });
      return;
    }

    Promise.all([
      taskApi.getById(id),
      userApi.getAll(),
      departmentApi.getAll(),
      settingsApiFull.get(),
    ])
      .then(([taskRes, usersRes, deptsRes, settingsRes]) => {
        const t = taskRes.data.data;
        setForm({
          title:        t.title || '',
          description:  t.description || '',
          assignedToId: t.assignedTo?.id || '',
          departmentId: t.department?.id || '',
          priority:     t.priority || 'MEDIUM',
          deadline:     t.deadline || '',
          tags:         t.tags || [],
        });
        setEmployees(usersRes.data.data || []);
        setDepts(deptsRes.data.data || []);
        setTags(settingsRes.data.data?.masterTags || []);
      })
      .catch(() => setError('Failed to load task'))
      .finally(() => setLoading(false));
  }, [id, user, navigate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleTag = (t) => setForm(f => ({
    ...f,
    tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t],
  }));

  const submit = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.deadline)     { setError('Deadline is required'); return; }

    const payload = {
      title:        form.title,
      description:  form.description,
      priority:     form.priority,
      deadline:     form.deadline,
      tags:         form.tags,
      assignedToId: form.assignedToId || null,
      departmentId: form.departmentId || null,
    };

    setError(''); setSaving(true);
    try {
      const res = await taskApi.update(id, payload);
      if (res.data.success) navigate(`/tasks/${id}`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const minDate = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const roster  = assignableEmployees(employees, user);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '640px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button onClick={() => navigate(`/tasks/${id}`)} style={S.backBtn}>← Back</button>
        <h1 style={S.title}>Edit Task</h1>
      </div>

      {error && <Alert type="error" message={error} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', marginTop: '16px' }}>
        <Input
          label="Title *"
          value={form.title}
          onChange={e => set('title', e.target.value)}
        />

        <div>
          <label style={S.label}>Description</label>
          <textarea
            value={form.description}
            onChange={e => set('description', e.target.value)}
            rows={3}
            style={{ ...S.input, resize: 'vertical' }}
          />
        </div>

        <div>
          <label style={S.label}>Assign to Employee</label>
          <select value={form.assignedToId} onChange={e => set('assignedToId', e.target.value)} style={S.input}>
            <option value="">— Unassigned (pool) —</option>
            {roster.map(e => (
              <option key={e.id} value={e.id}>
                {e.name} ({e.employeeId}){e.away ? ' · Away' : ''}
              </option>
            ))}
          </select>
          {user?.role === 'SUPER_ADMIN' && (
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '6px' }}>
              Super Admins manage tasks but are not listed as assignees.
            </p>
          )}
        </div>

        <div>
          <label style={S.label}>Department</label>
          <select value={form.departmentId} onChange={e => set('departmentId', e.target.value)} style={S.input}>
            <option value="">— Auto from employee —</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>

        <div>
          <label style={S.label}>Priority</label>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            {PRIORITIES.map(p => (
              <button key={p} onClick={() => set('priority', p)} style={{
                padding: '8px 16px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500,
                background: form.priority === p ? 'var(--color-primary)' : 'var(--color-surface)',
                color:      form.priority === p ? '#fff' : 'var(--color-text-secondary)',
                border:     `1.5px solid ${form.priority === p ? 'var(--color-primary)' : 'var(--color-border)'}`,
              }}>{p}</button>
            ))}
          </div>
        </div>

        <Input
          label="Deadline *"
          type="date"
          min={minDate}
          value={form.deadline}
          onChange={e => set('deadline', e.target.value)}
        />

        {tags.length > 0 && (
          <div>
            <label style={S.label}>Tags</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
              {tags.map(t => (
                <button key={t} onClick={() => toggleTag(t)} style={{
                  padding: '5px 12px', borderRadius: '999px', cursor: 'pointer',
                  fontSize: '12px', fontWeight: 500,
                  background: form.tags.includes(t) ? 'var(--color-primary)' : 'var(--color-surface)',
                  color:      form.tags.includes(t) ? '#fff' : 'var(--color-text-secondary)',
                  border:     `1px solid ${form.tags.includes(t) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                }}>{t}</button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <Btn variant="ghost" onClick={() => navigate(`/tasks/${id}`)}>Cancel</Btn>
          <Btn onClick={submit} loading={saving}>Save Changes</Btn>
        </div>
      </div>
    </div>
  );
}

const S = {
  title:  { fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)' },
  label:  { fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '6px' },
  input:  { width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '15px', color: 'var(--color-text)', background: 'var(--color-surface)' },
  backBtn:{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--color-text-muted)', padding: '4px' },
};
